import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Button, Container, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, IconButton, Tooltip, CircularProgress, Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField } from "@mui/material";
import { ArrowBack, PictureAsPdf, Code, Chat, Email, Replay, DeleteOutline } from "@mui/icons-material";
import clientBillingApi from "../../../api/clientBillingApi";
import { InvoiceStatusChip } from "../components/InvoiceStatusChip";
import { ClientInvoiceDetailResponse, ClientInvoiceStatus, formatCfdiUse } from "hvp-shared";

interface InvoiceDetail extends ClientInvoiceDetailResponse {
  ticketDisplay?: string;
  folioCfdi?: string;
  branch?: string;
  ticketDate?: string;
  requestedAt?: string;
  stampedAt?: string;
  dateRule?: string;
  warningMessage?: string;
  receiverRfc?: string;
  discount?: number;
}

export const InvoiceDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({ open: false, message: "", severity: "success" });
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [retrying, setRetrying] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const { data } = await clientBillingApi.get(`/client-billing/invoices/${id}`);
        setInvoice(data.data);
      } catch {
        setError("No se pudo cargar la factura");
      } finally {
        setIsLoading(false);
      }
    };
    fetchInvoice();
  }, [id]);

  const handleRetry = async () => {
    if (!id) return;
    try {
      setRetrying(true);
      const { data } = await clientBillingApi.post(`/client-billing/invoices/${id}/retry`);
      const newStatus = data?.data?.status;
      if (newStatus === ClientInvoiceStatus.stamped) {
        setSnackbar({ open: true, message: "Factura timbrada exitosamente", severity: "success" });
      } else if (newStatus === ClientInvoiceStatus.failed) {
        setSnackbar({
          open: true,
          message: data?.data?.errorMessage || "El reintento volvió a fallar",
          severity: "error",
        });
      } else {
        setSnackbar({ open: true, message: "Reintento en proceso", severity: "success" });
      }
      const refetched = await clientBillingApi.get(`/client-billing/invoices/${id}`);
      setInvoice(refetched.data.data);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error?.message || "Error al reintentar",
        severity: "error",
      });
    } finally {
      setRetrying(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      setDeleting(true);
      await clientBillingApi.delete(`/client-billing/invoices/${id}`);
      setDeleteOpen(false);
      navigate("/facturacion");
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error?.message || "Error al eliminar",
        severity: "error",
      });
      setDeleting(false);
    }
  };

  const handleDownload = async (type: "pdf" | "xml") => {
    try {
      const response = await clientBillingApi.get(`/client-billing/invoices/${id}/${type}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `factura-${invoice?.invoiceNumber}.${type}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setSnackbar({ open: true, message: "Error al descargar", severity: "error" });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !invoice) {
    return (
      <Container maxWidth="md">
        <Alert severity="error">{error || "Factura no encontrada"}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/facturacion")} sx={{ mt: 2 }}>
          Volver
        </Button>
      </Container>
    );
  }

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });

  return (
    <Container maxWidth="md">
      <Button startIcon={<ArrowBack />} onClick={() => navigate("/facturacion")} sx={{ mb: 2 }}>
        Volver a mis facturas
      </Button>

      <Paper elevation={2} sx={{ p: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Ticket {invoice.ticketDisplay || invoice.invoiceNumber}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {invoice.customerName}
              {invoice.branch && ` — ${invoice.branch}`}
            </Typography>
            {invoice.folioCfdi && (
              <Typography variant="body2" color="textSecondary">
                Folio CFDI: {invoice.folioCfdi}
              </Typography>
            )}
          </Box>
          <InvoiceStatusChip status={invoice.status} />
        </Box>

        <Box sx={{ display: "flex", gap: 4, mb: 3, flexWrap: "wrap" }}>
          {invoice.ticketDate && (
            <Box>
              <Typography variant="caption" color="textSecondary">Fecha del ticket</Typography>
              <Typography variant="body2">{formatDate(invoice.ticketDate)}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="textSecondary">Fecha de solicitud</Typography>
            <Typography variant="body2">{formatDate(invoice.requestedAt || invoice.createdAt)}</Typography>
          </Box>
          {invoice.stampedAt && (
            <Box>
              <Typography variant="caption" color="textSecondary">Fecha de timbrado</Typography>
              <Typography variant="body2">{formatDate(invoice.stampedAt)}</Typography>
            </Box>
          )}
        </Box>

        {invoice.dateRule === "previous_month" && invoice.status === ClientInvoiceStatus.requires_authorization && (
          <Paper sx={{ p: 3, mb: 3, backgroundColor: "#fff3e0", border: "1px solid #ffb74d" }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Esta factura requiere autorización
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              La compra es de un periodo fiscal anterior ({invoice.ticketDate ? formatDate(invoice.ticketDate) : "fecha desconocida"}). Para procesarla, necesitas solicitar autorización por WhatsApp.
            </Typography>
            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={<Chat />}
              href={`https://wa.me/5219991841434?text=${encodeURIComponent(`Hola, solicito autorización para facturar el ticket ${invoice.ticketDisplay || invoice.invoiceNumber} del ${invoice.ticketDate ? formatDate(invoice.ticketDate) : "periodo anterior"}. RFC: ${invoice.receiverRfc || ""}. Cliente: ${invoice.customerName || ""}. Sucursal: ${invoice.branch || ""}. Total: $${invoice.total?.toFixed(2)}`)}`}
              target="_blank"
              sx={{ borderRadius: 2 }}
            >
              Solicitar por WhatsApp
            </Button>
          </Paper>
        )}

        {invoice.warningMessage && invoice.dateRule === "same_month" && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">{invoice.warningMessage}</Typography>
          </Alert>
        )}

        {invoice.errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {invoice.errorMessage}
          </Alert>
        )}

        {invoice.status === ClientInvoiceStatus.failed && (
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Replay />}
              onClick={handleRetry}
              disabled={retrying}
            >
              {retrying ? "Reintentando..." : "Reintentar"}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutline />}
              onClick={() => setDeleteOpen(true)}
              disabled={retrying}
            >
              Eliminar solicitud
            </Button>
          </Box>
        )}

        {invoice.receiverCfdiUse && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="textSecondary">Uso del CFDI</Typography>
            <Typography variant="body1">{formatCfdiUse(invoice.receiverCfdiUse)}</Typography>
          </Box>
        )}

        {invoice.cfdiUuid && (
          <Box sx={{ mb: 3, p: 2, backgroundColor: "#f0f7ff", borderRadius: 1 }}>
            <Typography variant="body2" color="textSecondary">UUID Fiscal</Typography>
            <Typography variant="body1" fontFamily="monospace" fontSize={13}>
              {invoice.cfdiUuid}
            </Typography>
          </Box>
        )}

        {invoice.lineItems && invoice.lineItems.length > 0 && (
          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Concepto</TableCell>
                  <TableCell align="right">Cant.</TableCell>
                  <TableCell align="right">P. Unit.</TableCell>
                  <TableCell align="right">IVA</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoice.lineItems.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{item.productName}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">${item.unitPrice.toFixed(2)}</TableCell>
                    <TableCell align="right">{item.taxRate}%</TableCell>
                    <TableCell align="right">${item.total.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} />
                  <TableCell align="right"><strong>Subtotal</strong></TableCell>
                  <TableCell align="right"><strong>${invoice.subtotal?.toFixed(2)}</strong></TableCell>
                </TableRow>
                {(invoice.discount ?? 0) > 0 && (
                  <TableRow>
                    <TableCell colSpan={3} />
                    <TableCell align="right"><strong>Descuento</strong></TableCell>
                    <TableCell align="right" sx={{ color: "error.main" }}><strong>-${invoice.discount?.toFixed(2)}</strong></TableCell>
                  </TableRow>
                )}
                {invoice.taxTotal > 0 && (
                  <TableRow>
                    <TableCell colSpan={3} />
                    <TableCell align="right"><strong>IVA</strong></TableCell>
                    <TableCell align="right"><strong>${invoice.taxTotal?.toFixed(2)}</strong></TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell colSpan={3} />
                  <TableCell align="right"><strong>Total</strong></TableCell>
                  <TableCell align="right"><strong>${invoice.total?.toFixed(2)}</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {invoice.status === ClientInvoiceStatus.stamped && (
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            <Button variant="contained" color="error" startIcon={<PictureAsPdf />} onClick={() => handleDownload("pdf")}>
              Descargar PDF
            </Button>
            <Button variant="contained" color="primary" startIcon={<Code />} onClick={() => handleDownload("xml")}>
              Descargar XML
            </Button>
            <Button variant="outlined" startIcon={<Email />} onClick={() => { setEmailDialogOpen(true); setEmailInput(""); }}>
              Enviar a otro correo
            </Button>
          </Box>
        )}
      </Paper>
      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)}>
        <DialogTitle>Enviar factura por correo</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Ingresa el correo electrónico al que deseas enviar el PDF y XML de esta factura.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Email"
            type="email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && emailInput) {
                setEmailDialogOpen(false);
                clientBillingApi.post(`/client-billing/invoices/${id}/send-email`, { email: emailInput })
                  .then(() => setSnackbar({ open: true, message: "Email enviado", severity: "success" }))
                  .catch((err: any) => setSnackbar({ open: true, message: err.response?.data?.error?.message || "Error al enviar", severity: "error" }));
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" disabled={!emailInput} onClick={() => {
            setEmailDialogOpen(false);
            clientBillingApi.post(`/client-billing/invoices/${id}/send-email`, { email: emailInput })
              .then(() => setSnackbar({ open: true, message: "Email enviado", severity: "success" }))
              .catch((err: any) => setSnackbar({ open: true, message: err.response?.data?.error?.message || "Error al enviar", severity: "error" }));
          }}>Enviar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)}>
        <DialogTitle>Eliminar solicitud</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que deseas eliminar esta solicitud con error? Podrás volver a solicitarla después.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
};
